import { useEffect } from "react";
import { api, useApiMutation } from "../../api";
import type { Company } from "../../api";
import { Card, useToast } from "../../components";
import feuilles from "../feuilles/Feuilles.module.css";

export type ValidationFeuillesProps = {
  company: Company;
  /** Seul l'administrateur ESN change le réglage ; les autres le lisent. */
  editable: boolean;
  onUpdated: (c: Company) => void;
};

/**
 * Le circuit des feuilles de temps de l'entreprise : imputées dès la
 * soumission (le défaut), ou validées d'abord par le client ou l'ESN.
 * Couper la validation ne touche pas les feuilles déjà soumises : elles
 * attendent toujours une décision.
 */
export function ValidationFeuilles({ company, editable, onUpdated }: ValidationFeuillesProps) {
  const { notify, notifyError } = useToast();
  const reglage = useApiMutation(api.updateCompanySettings);

  // Le refus se lit au rendu qui suit l'échec : juste après `run`, `reglage.error`
  // vaut encore celui du rendu précédent (voir useApiMutation).
  useEffect(() => {
    if (reglage.error !== null) {
      notifyError(reglage.error, "Réglage non enregistré");
    }
  }, [reglage.error, notifyError]);

  async function basculer(active: boolean) {
    const maj = await reglage.run(company.id, { timesheet_validation: active });
    if (maj === null) {
      return;
    }
    onUpdated(maj);
    notify({
      tone: "success",
      title: active ? "Validation des feuilles activée" : "Validation des feuilles coupée",
      description: active
        ? "Les prochaines feuilles attendront une décision avant d'être imputées."
        : "Les prochaines feuilles seront imputées dès leur soumission.",
    });
  }

  return (
    <Card
      title="Validation des feuilles de temps"
      subtitle="Sans validation, une feuille soumise est imputée aussitôt sur l'enveloppe de la mission, et l'entreprise en est simplement informée."
    >
      <label className={`${feuilles.case} ${editable ? "" : feuilles.caseGrisee}`}>
        <input
          type="checkbox"
          checked={company.timesheet_validation}
          disabled={!editable || reglage.pending}
          onChange={(e) => void basculer(e.target.checked)}
        />
        <span>Faire valider chaque feuille avant imputation</span>
        {!editable && <span className={feuilles.droit}>réglé par un administrateur ESN</span>}
      </label>
    </Card>
  );
}
